import { Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { api_routes, db_tables } from '@app/consts';
import { OidcAuthService } from '@app/core/services/oidc-auth.service';
import { SnackbarService } from '@app/core/services/snackbar.service';
import { GoalDialogComponent } from '@app/modules/performance/components/goal-dialog/goal-dialog.component';
import { AllGoalsListItem, EmployeeGoal, EmployeeGoalSubmit } from '@app/modules/performance/components/goal-plans/models/goal-plan.model';
import { GoalPlanService } from '@app/modules/performance/components/goal-plans/services/goal-plan.service';
import { OverlayService } from '@app/shared/components/overlay/overlay.service';
import { TranslateService } from '@ngx-translate/core';
import { GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import { GridState } from '@app/shared/services/grid-state/grid-state.model';
import moment from 'moment';
import { defer, finalize, forkJoin, from } from 'rxjs';

@Component({
  selector: 'app-all-goals-list',
  templateUrl: './all-goals-list.component.html',
  styleUrls: ['./all-goals-list.component.scss']
})
export class AllGoalsListComponent implements OnInit {
  @ViewChild('excelexport') excelexport: any;

  excelColumns: any[] = [
    { field: 'employee', title: this.translate.instant("EmployeeGoals-Employee") },
    { field: 'objective', title: this.translate.instant("EmployeeGoals-Objective") },
    { field: 'description', title: this.translate.instant("EmployeeGoals-Description") },
    { field: 'expectedCompletionDate', title: this.translate.instant("EmployeeGoals-ExpectedCompletionDate") },
    { field: 'actualCompletionDate', title: this.translate.instant("EmployeeGoals-ActualCompletionDate") },
    { field: 'goalType.name', title: this.translate.instant("EmployeeGoals-GoalType") },
    { field: 'goalPlan.name', title: this.translate.instant("EmployeeGoals-GoalPlan") },
    { field: 'createdBy', title: this.translate.instant("EmployeeGoals-CreatedBy") },
    { field: 'parent.objective', title: this.translate.instant("EmployeeGoals-Parent") },
    { field: 'percentageComplete', title: this.translate.instant("EmployeeGoals-PercentageComplete") },
    { field: 'weight', title: this.translate.instant("EmployeeGoals-Weight") },
    { field: 'managers', title: this.translate.instant("EmployeeGoals-Managers") }
  ];

  public columns: any[] = [
    { field: "employee", tableId: "", title: "Employee", type: "employee" },
    { field: "objective", tableId: "tfi_PEgObjective", title: this.translate.instant("EmployeeGoals-Objective") },
    { field: "description", tableId: "tfi_PEgDescription", title: "Description", type: "longText", longTextLimit: 200 },
    { field: "expectedCompletionDate", tableId: "tfi_PEgExpectedCompletionDate", title: "Expected Completion Date", type: "date", dataType: 'Date' },
    { field: "actualCompletionDate", tableId: "tfi_PEgActualCompletionDate", title: "Actual Completion Date", type: "date", dataType: 'Date' },
    { field: "goalType", subField: "name", tableId: "tfi_PEgGoalType", title: "Goal Type", dataType: "SpecialLookup", lookupCode: 'GOAL_TYPE' },
    { field: "goalPlan", subField: "name", tableId: "", title: this.translate.instant("EmployeeGoals-GoalPlan"), dataType: "SpecialLookup", lookupCode: 'GOAL_PLAN' },
    { field: "createdBy", tableId: "", title: this.translate.instant("EmployeeGoals-CreatedBy"), type: "employee" },
    { field: "parent", subField: "objective", tableId: "tfi_PEgParentGoal", title: this.translate.instant("EmployeeGoals-Parent") },
    { field: "percentageComplete", tableId: "tfi_PEgPercentageComplete", title: "Percentage Complete", type: "percentage", percentageMaxValue: 1 },
    { field: "weight", tableId: "tfi_PEgWeight", title: "Weight", type: "percentage", percentageMaxValue: 1 },
    { field: "managers", tableId: "", title: "Managers", type: "employees" },
  ];

  filterCategories: any[] = [
    { field: "description", tableId: "tfi_PEgDescription", title: "Description", type: "String", dataType: 'String' },
    { field: "Employee.FirstName", title: "Employee First Name", type: "String", dataType: 'String' },
    { field: "Employee.LastName", title: "Employee Last Name", type: "String", dataType: 'String' },
    { field: "Manager.FirstName", title: "Manager First Name", type: "String", dataType: 'String' },
    { field: "Manager.LastName", title: "Manager Last Name", type: "String", dataType: 'String' },
    { field: "PersonalGoal", title: "Is Personal Goal", type: "Boolean", dataType: 'Boolean' },
    { field: "expectedCompletionDate", tableId: "tfi_PEgExpectedCompletionDate", title: "Expected Completion Date", type: "date", dataType: 'Date' },
    { field: "actualCompletionDate", tableId: "tfi_PEgActualCompletionDate", title: "Actual Completion Date", type: "date", dataType: 'Date' },
    { field: "GoalTypeId", subField: "name", tableId: "tfi_PEgGoalType", title: "Goal Type", dataType: "SpecialLookup", lookupCode: 'GOAL_TYPE' },
    { field: "GoalPlanId", subField: "name", tableId: "", title: "Goal Plan", dataType: "SpecialLookup", lookupCode: 'GOAL_PLAN' },
    { field: "percentageComplete", tableId: "tfi_PEgPercentageComplete", title: "Percentage Complete", dataType: "Percentage" },
    { field: "weight", tableId: "tfi_PEgWeight", title: "Weight", dataType: "Percentage" },
  ];


  sortableColumns: any[] = [
    { field: "objective", sortValue: "objective" },
    { field: "description", sortValue: "description" },
    { field: "percentageComplete", sortValue: "percentageComplete" },
    { field: "weight", sortValue: "weight" },
    { field: "expectedCompletionDate", sortValue: "expectedCompletionDate" },
    { field: "actualCompletionDate", sortValue: "actualCompletionDate" },
    { field: "goalType", sortValue: "GoalTypeName" },
    { field: "goalPlan", sortValue: "GoalPlanName" },
  ];

  public gridId: string = 'hubbubhr-all-goals-grid';
  public gridData: any;
  public gridDataResult: GridDataResult;
  public api_routes = api_routes;
  public db_tables = db_tables;
  isLoading: boolean;
  pageSize: number = 10;
  skip: number = 0;
  clearSelectedItems: boolean = false;
  dialogRef: any;
  formValid: any;
  user$: any;

  getGoalsRequest: any;
  filterString: string;
  searchFilterString: string;
  searchValue: string = "";
  sortString: string;
  fileName: string = 'Goals.xlsx';
  excelData: any[];

  constructor(
    private dialog: MatDialog,
    public translate: TranslateService,
    private snackbarService: SnackbarService,
    private overlayService: OverlayService,
    private oidcAuthService: OidcAuthService,
    private goalService: GoalPlanService,
  ) {
    this.user$ = this.oidcAuthService.userProfile;
  }

  ngOnInit(): void {
    this.getGoals();
  }

  search(newSearchString: string) {
    this.searchValue = newSearchString;

    let variants = this.searchValue.split(' ').filter(value => value);
    this.searchFilterString = '';
    variants.map(variant => {
      if (variant) {
        if (this.searchFilterString.length > 2) {
          this.searchFilterString += ' AND ';
        }
        this.searchFilterString += `((EmployeeFirstName like "${variant}") OR (EmployeeLastName like "${variant}") OR (EmployeeId like "${variant}") OR (Objective like "${variant}") OR (Description like "${variant}"))`;
      }
    });

    this.getGoalsRequest.unsubscribe();
    this.getGoals();
  }

  getGoals() {
    let filter;
    if (this.searchFilterString) {
      filter = this.searchFilterString;
    }
    if (this.filterString) {
      if (filter) {
        filter = `${filter} AND ${this.filterString}`;
      }
      else {
        filter = `${this.filterString}`;
      }
    }

    this.isLoading = true;

    this.getGoalsRequest = this.goalService.getAllGoals(this.pageSize, this.skip, this.sortString, filter)
      .pipe(
        finalize(() => this.isLoading = false)
      )
      .subscribe(
        (res) => {

          this.gridDataResult = {
            data: res.data,
            total: res.total,
          }

          // If the goal doesnt have a parent mark it as a sub goal
          this.gridDataResult.data.forEach(
            goal => {
              if (goal.parent === null) {
                goal.isSubGoal = false;
              }
              else {
                goal.isSubGoal = true;
              }
            }
          )

          this.gridData = res.data;
        }
      );
  }

  public allData() {
    this.overlayService.show('Loading');

    from(this.goalService.getAllGoalsAsync(this.sortString, this.filterString))
      .subscribe({
        next: (res) => {
          this.excelData = res.data;

          this.excelData.map((item) => {
            item.employee = `${item.employee.firstName} ${item.employee.lastName}`;
            item.goalType = item.goalType?.name;
            item.goalPlan = item.goalPlan?.name;
            item.expectedCompletionDate = item.expectedCompletionDate ? moment(item.expectedCompletionDate).format('YYYY-MM-DD') : null;
            item.actualCompletionDate = item.actualCompletionDate ? moment(item.actualCompletionDate).format('YYYY-MM-DD') : null;
            item.percentageComplete = item.percentageComplete ? `${item.percentageComplete * 100}%` : null;
            item.weight = item.weight ? `${item.weight * 100}%` : null;
            item.managers = item.managers?.map(manager => `${manager.firstName} ${manager.lastName}`).join(', ');
            item.createdBy = `${item.createdBy.firstName} ${item.createdBy.lastName}`;
            item.parent = item.parent?.objective;
          });

          // if I dont have this timeout first time I download the report the data will be blank
          setTimeout(() => {
            this.excelexport.save();
          }, 1000);
        },
        error: (e) => {
          this.overlayService.hide();
        },
        complete: () => this.overlayService.hide()
      });
  }

  getGoalVerbose(goal?: AllGoalsListItem) {
    if (goal === undefined) {
      this.openGoalDialog();
    }
    else {
      let employeeGoal: EmployeeGoal;

      this.isLoading = true;

      this.goalService.getEmployeeGoal(goal.employee?.id, goal.id)
        .pipe(
          finalize(() => this.isLoading = false)
        )
        .subscribe(
          res => {
            employeeGoal = res;
            employeeGoal.employee.id = goal.employee?.id;
            this.openGoalDialog(employeeGoal);
          }
        );
    }
  }

  openGoalDialog(goal?: EmployeeGoal) {
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;

    dialogConfig.data = {
      goal: goal,
      employeeId: goal.employee?.id,
      readOnly: true
    };

    this.dialogRef = this.dialog.open(GoalDialogComponent, dialogConfig);

    this.dialogRef.afterClosed().subscribe(
      data => {
        if (data === true) {
          this.getGoals();
        }
      }
    );
  }

  createNewGoal(formData: EmployeeGoalSubmit) {
    this.overlayService.show();

    this.goalService.createEmployeeGoal(this.user$.userId, formData)
      .pipe(
        finalize(() => this.overlayService.hide())
      )
      .subscribe(
        (res) => {
          this.snackbarService.openSnackBar(`${this.translate.instant('SavedSuccessfully')}`, 'clear', 'success');
          this.dialogRef.close(res);
          this.getGoals();
        }
      );
  }

  updateGoal(formData: EmployeeGoalSubmit) {
    this.overlayService.show();

    this.goalService.updateEmployeeGoal(this.user$.userId, formData)
      .pipe(
        finalize(() => this.overlayService.hide())
      )
      .subscribe(
        (res) => {
          this.snackbarService.openSnackBar(`${this.translate.instant('SavedSuccessfully')}`, 'clear', 'success');
          this.dialogRef.close(res);
          this.getGoals();
        }
      );
  }

  deleteGoals(goalIds: string[]) {
    const observables = goalIds.map(selectedItem => defer(() => this.goalService.deleteEmployeeGoal(this.user$.userId, selectedItem)));

    this.overlayService.show();

    forkJoin(observables)
      .pipe(
        finalize(() => this.overlayService.hide())
      )
      .subscribe(
        (res) => {
          this.getGoals();
          this.clearSelectedItems = !this.clearSelectedItems;
          this.snackbarService.openSnackBar(`${this.translate.instant('DeletedSuccessfully')}`, 'clear', 'success');
        }
      );
  }

  gridStateChange(gridState: GridState) {
    if (this.pageSize !== gridState.pageSize) {
      this.pageSize = gridState.pageSize;
    }
    if (this.skip !== gridState.skip) {
      this.skip = gridState.skip;
    }
    if (this.sortString !== gridState.sortString) {
      this.sortString = gridState.sortString;
    }
    if (this.filterString !== gridState.filter) {
      this.filterString = gridState.filter;
      this.skip = 0;
    }

    if (this.searchValue !== gridState.searchValue) {
      let variants = [];
      this.searchFilterString = '';
      this.searchValue = gridState.searchValue;

      if (this.searchValue !== null) {
        variants = this.searchValue.split(' ').filter(value => value);
      }

      variants.map(variant => {
        if (variant) {
          if (this.searchFilterString.length > 2) {
            this.searchFilterString += ' AND ';
          }
          this.searchFilterString += `((EmployeeFirstName like "${variant}") OR (EmployeeLastName like "${variant}") OR (EmployeeId like "${variant}") OR (Objective like "${variant}") OR (Description like "${variant}"))`;
        }
      });

      this.skip = 0;
    }

    if (this.getGoalsRequest) {
      this.getGoalsRequest.unsubscribe();
    }
    this.getGoals();
  }
}
